import { Component, Inject, HostListener, Renderer2 } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface ViewerPhoto {
    id: number;
    url: string;
    width?: number;
    height?: number;
    blurred?: boolean;
}

export interface MintGirlViewerData {
    photos: ViewerPhoto[];
    index: number; // index
    girlName?: string;
}

@Component({
    selector: 'app-mint-girl-viewer',
    templateUrl: './mint-girl-viewer.component.html',
    styleUrls: ['./mint-girl-viewer.component.scss']
})
export class MintGirlViewerComponent {
    photos: ViewerPhoto[] = [];
    index = 0;
    girlName?: string;

    // ===== Swipe state =====
    private dragging = false;
    private startX = 0;
    private startY = 0;
    private deltaX = 0;
    private deltaY = 0;
    private hasMovedHoriz = false;
    private readonly SWIPE_THRESHOLD = 50; // px to commit swipe
    dragTransform = '';
    dragTransition = '';

    constructor(
        private dialogRef: MatDialogRef<MintGirlViewerComponent>,
        @Inject(MAT_DIALOG_DATA) data: MintGirlViewerData,
        private renderer: Renderer2
    ) {
        this.photos = data.photos ?? [];
        this.index = Math.min(Math.max(data.index ?? 0, 0), this.photos.length - 1);
        this.girlName = data.girlName;
    }

    get current(): ViewerPhoto | undefined { return this.photos[this.index]; }

    close() { this.dialogRef.close(); }
    prev() {
        const n = this.photos.length;
        if (!n) return;
        this.index = (this.index - 1 + n) % n;   // jump from index first to last
    }

    next() {
        const n = this.photos.length;
        if (!n) return;
        this.index = (this.index + 1) % n;       // jump from index last to first
    }

    // ==== Pointer Events ====
    onPointerDown(e: PointerEvent) {
        if (e.isPrimary === false) return;
        this.dragging = true;
        this.hasMovedHoriz = false;
        this.dragTransition = '';
        this.startX = e.clientX;
        this.startY = e.clientY;
        this.deltaX = 0;
        this.deltaY = 0;

        // capture pointer so it still can receive movement
        (e.target as Element)?.setPointerCapture?.(e.pointerId);
    }

    onPointerMove(e: PointerEvent) {
        if (!this.dragging) return;
        this.deltaX = e.clientX - this.startX;
        this.deltaY = e.clientY - this.startY;

        if (!this.hasMovedHoriz) {
            this.hasMovedHoriz = Math.abs(this.deltaX) > Math.abs(this.deltaY) && Math.abs(this.deltaX) > 4;
        }
        if (!this.hasMovedHoriz) return;

        e.preventDefault();
        this.dragTransform = `translate3d(${this.deltaX}px, 0, 0)`;
    }

    onPointerUp(e: PointerEvent) {
        if (!this.dragging) return;
        const commitLeft = this.deltaX > this.SWIPE_THRESHOLD && this.hasMovedHoriz;
        const commitRight = this.deltaX < -this.SWIPE_THRESHOLD && this.hasMovedHoriz;

        this.dragTransition = 'transform .18s ease-out';
        if (commitLeft) {
            // slide to prev photos
            this.dragTransform = 'translate3d(100vw, 0, 0)';
            setTimeout(() => this.prev(), 160);
        } else if (commitRight) {
            // slide to next photos
            this.dragTransform = 'translate3d(-100vw, 0, 0)';
            setTimeout(() => this.next(), 160);
        } else {
            // bounce back
            this.dragTransform = 'translate3d(0, 0, 0)';
            setTimeout(() => this.resetDrag(), 180);
        }
        this.dragging = false;
    }

    onPointerCancel(_: PointerEvent) {
        if (!this.dragging) return;
        this.dragTransition = 'transform .15s ease-out';
        this.dragTransform = 'translate3d(0, 0, 0)';
        setTimeout(() => this.resetDrag(), 150);
        this.dragging = false;
    }

    // ==== Touch fallback ====
    private firstTouch(ev: TouchEvent) {
        const t = ev.changedTouches && ev.changedTouches[0];
        return t ? { x: t.clientX, y: t.clientY } : { x: 0, y: 0 };
    }
    onTouchStart(ev: TouchEvent) {
        const p = this.firstTouch(ev);
        this.dragging = true;
        this.hasMovedHoriz = false;
        this.dragTransition = '';
        this.startX = p.x;
        this.startY = p.y;
        this.deltaX = 0;
        this.deltaY = 0;
    }
    onTouchMove(ev: TouchEvent) {
        if (!this.dragging) return;
        const p = this.firstTouch(ev);
        this.deltaX = p.x - this.startX;
        this.deltaY = p.y - this.startY;

        if (!this.hasMovedHoriz) {
            this.hasMovedHoriz = Math.abs(this.deltaX) > Math.abs(this.deltaY) && Math.abs(this.deltaX) > 4;
        }
        if (!this.hasMovedHoriz) return;

        ev.preventDefault();
        this.dragTransform = `translate3d(${this.deltaX}px, 0, 0)`;
    }
    onTouchEnd(_: TouchEvent) {
        if (!this.dragging) return;
        const commitLeft = this.deltaX > this.SWIPE_THRESHOLD && this.hasMovedHoriz;
        const commitRight = this.deltaX < -this.SWIPE_THRESHOLD && this.hasMovedHoriz;

        this.dragTransition = 'transform .18s ease-out';
        if (commitLeft) {
            this.dragTransform = 'translate3d(100vw, 0, 0)';
            setTimeout(() => this.prev(), 160);
        } else if (commitRight) {
            this.dragTransform = 'translate3d(-100vw, 0, 0)';
            setTimeout(() => this.next(), 160);
        } else {
            this.dragTransform = 'translate3d(0, 0, 0)';
            setTimeout(() => this.resetDrag(), 180);
        }
        this.dragging = false;
    }

    // ==== util ====
    private resetDrag() {
        this.deltaX = this.deltaY = 0;
        this.dragTransform = '';
        this.dragTransition = '';
        this.dragging = false;
        this.hasMovedHoriz = false;
    }

    @HostListener('window:keydown', ['$event'])
    onKey(e: KeyboardEvent) {
        if (e.key === 'Escape') this.close();
        if (e.key === 'ArrowLeft') this.prev();
        if (e.key === 'ArrowRight') this.next();
    }

    async downloadCurrent() {
        const p = this.current;
        if (!p) return;

        try {
            // get blob— include credentials if needed
            const res = await fetch(p.url, { credentials: 'include' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = this.suggestFilename(p);
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            // Fallback (Safari/iOS/webview need this)
            window.open(p.url, '_blank');
        }
    }

    private suggestFilename(p: ViewerPhoto) {
        // get name from url; if failed, use default
        try {
            const u = new URL(p.url, window.location.origin);
            const base = u.pathname.split('/').pop() || `photo-${p.id}.jpg`;
            return base.includes('.') ? base : `${base}.jpg`;
        } catch {
            return `photo-${p.id}.jpg`;
        }
    }
}